<template>
  <v-layout full-height>
    <v-navigation-drawer
      v-model="drawer"
      elevation="5"
      color="purple-1"
      :temporary="mobile"
      :width="280"
    >
      <v-list>
        <router-link to="/" class="d-flex flex-grow-1 px-5 mt-4 mb-8">
          <v-img
            src="@/assets/img/yetipay-logo-white.svg"
            alt="yetipay"
            width="154px"
            height="46px"
          />
        </router-link>

        <div v-for="item in menu" :key="item.title">
          <v-list-group
            v-if="item.pages && (!item.hasAccess || item.hasAccess())"
            :value="item.title"
          >
            <template #activator="{ props }">
              <v-list-item v-bind="props" :prepend-icon="item.icon" :title="item.title" />
            </template>
            <v-list-item
              v-for="page in item.pages.filter(
                (page) => (!page.hasAccess || page.hasAccess()) && !page.hide,
              )"
              :key="page.title"
              :to="page.to"
              :title="page.title"
              link
              @click="page.atClick"
            />
          </v-list-group>
          <v-list-item
            v-else-if="!item.hasAccess || item.hasAccess()"
            :to="item.to"
            :title="item.title"
            link
            :prepend-icon="item.icon"
          />
        </div>
      </v-list>
      <template #append>
        <StaffOverride v-if="authorizationStore.isStaff" />

        <v-select
          v-else-if="userHasMultipleCustomers"
          v-model="selectedMerchant"
          :items="merchantsStore.merchants"
          item-title="legalEntityName"
          item-value="merchantId"
          bg-color="background"
          density="compact"
          rounded="sm"
          class="mx-3"
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props" :append-icon="merchantDropdownIcon(item.raw as Merchant)" />
          </template>
        </v-select>

        <div v-else class="pa-3 text-center">
          {{ merchantsStore.currentMerchant?.legalEntityName }}
        </div>

        <NewCompanyConfirmation v-model="addCompanyModal" />
      </template>
    </v-navigation-drawer>

    <v-app-bar
      elevation="0"
      :color="(!merchantsStore.overridden && 'background') || undefined"
      :class="`${!mobile && 'px-11'} ${merchantsStore.overridden && 'staff-warning-blink'}`"
    >
      <v-btn v-if="mobile" icon offset-y @click="drawer = !drawer">
        <v-icon color="primary"> mdi-menu </v-icon>
      </v-btn>
      <v-breadcrumbs v-if="!mobile" :items="breadcrumbs">
        <template #prepend>
          <RouterLink to="/" class="d-flex no-underline mr-3">
            <v-icon left color="primary"> mdi-home </v-icon>
          </RouterLink>
        </template>
      </v-breadcrumbs>

      <template #append>
        <v-btn href="https://linktr.ee/yetipay.me" target="_blank">
          {{ t("app.support") }}<v-icon class="ml-1" color="primary"> mdi-face-agent </v-icon>
        </v-btn>
        <v-menu offset-y>
          <template #activator="{ props }">
            <v-btn icon v-bind="props">
              <v-icon color="primary"> mdi-account-circle </v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title>
                <RouterLink
                  :to="`/users/${authStore.user.id}`"
                  class="text-decoration-none text-black"
                >
                  <v-icon left> mdi-at </v-icon> {{ authStore.user.email }}
                </RouterLink>
              </v-list-item-title>
            </v-list-item>
            <LocaleSelector />
            <v-list-item link @click.prevent="logout">
              <v-list-item-title
                ><v-icon>mdi-logout</v-icon> {{ t("layouts.app.signOut") }}</v-list-item-title
              >
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-app-bar>

    <v-main style="height: 100vh; overflow: auto">
      <router-view v-slot="slot">
        <transition name="fade" mode="out-in">
          <div :key="route.path">
            <MainContent :name="slot.route.name as string" color="background">
              <component :is="slot.Component" />
            </MainContent>
          </div>
        </transition>
      </router-view>
    </v-main>

    <Footer />
  </v-layout>
</template>

<script setup lang="ts">
import { PostHog } from "posthog-js";
import { ref, computed, watch, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { Merchant } from "@/api/merchant-service/merchant";
import { useApplicationStore } from "@/store/application";
import { useAuthenticationStore } from "@/store/authentication";
import { Permission, useAuthorizationStore } from "@/store/authorization";
import { useMerchantsStore } from "@/store/merchants";

const posthog = inject<PostHog>("posthog");

const route = useRoute();
const router = useRouter();
const appStore = useApplicationStore();
const authStore = useAuthenticationStore();
const authorizationStore = useAuthorizationStore();
const merchantsStore = useMerchantsStore();
const { mobile } = useDisplay();
const { t } = useI18n();

const breadcrumbs = computed(() =>
  (route.meta.breadcrumbs ?? [{ titleKey: "breadcrumbs.home" }]).map((breadcrumb) => {
    return {
      title: t(breadcrumb.titleKey),
      href: breadcrumb.href,
    };
  }),
);
const merchantDropdownIcon = (merchant: Merchant) =>
  merchantsStore.hasMerchantBeenActivated(merchant)
    ? merchantsStore.hasMerchantChurned(merchant)
      ? "mdi-robot-dead"
      : undefined
    : "mdi-alert";

const addCompanyModal = ref(false);
const logout = async () => {
  await authStore.signOut();
  router.push("/signin");
};

const userHasMultipleCustomers = computed(() => (merchantsStore.merchants ?? []).length > 1);
const selectedMerchant = ref(merchantsStore.currentMerchant?.merchantId);

// Add user to PostHog group for their currentMerchant
if (merchantsStore.currentMerchant) {
  const formattedLegalEntityName = (merchantsStore.currentMerchant.legalEntityName ?? "").replace(
    /[^a-zA-Z0-9 ]/g,
    "",
  );
  posthog?.group("company", formattedLegalEntityName, {
    merchantId: merchantsStore.currentMerchant.merchantId,
    selectedIndustries: merchantsStore.currentMerchant.selectedIndustries.join(""),
    country: merchantsStore.currentMerchant.country,
    type: merchantsStore.currentMerchant.type,
  });
}

watch(selectedMerchant, (value) => {
  try {
    merchantsStore.switch(value);
    window.location.href = "/"; // Hard reload the app, simpler than trying to update all the relevant stores
  } catch (error) {
    console.error(error);
    appStore.notifyUser({ message: "Failed to switch customer. Contact support.", type: "error" });
  }
});

const drawer = ref(!mobile.value);

interface Menu {
  title: string;
  icon: string;
  to?: string;
  hasAccess?: () => boolean;
  pages?: Array<{
    title: string;
    to?: string;
    atClick?: () => void;
    hasAccess?: () => boolean;
    hide?: boolean;
  }>;
}
const menu = ref<Menu[]>([
  {
    title: t("menu.devices.title"),
    icon: "mdi-cellphone",
    hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Device_ListDevices),
    pages: [
      {
        title: t("menu.devices.allDevices"),
        to: "/devices",
        hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Device_ListDevices),
      },
      {
        title: t("menu.devices.orderNewDevice"),
        to: "/request-new-device",
        hasAccess: () => authorizationStore.hasCompanyScoped(Permission.Device_OrderDevice),
      },
      {
        title: t("menu.devices.connectNewDevice"),
        to: "/assign-new-device",
        hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Device_AssignDevice),
      },
    ],
  },
  {
    title: t("menu.sites.title"),
    icon: "mdi-store",
    hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Site_ListSites),
    pages: [
      {
        title: t("menu.sites.allSites"),
        to: "/sites",
        hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Site_ListSites),
      },
      {
        title: t("menu.sites.newSite"),
        to: "/new-site",
        hasAccess: () => authorizationStore.hasCompanyScoped(Permission.Site_CreateSite),
      },
    ],
  },
  {
    title: t("menu.reporting.title"),
    icon: "mdi-chart-bar",
    pages: [
      {
        title: t("menu.reporting.transactions"),
        to: "/transactions",
        hasAccess: () =>
          authorizationStore.hasAnySiteScoped(Permission.Transaction_ListTransactions),
      },
      {
        title: t("menu.reporting.payouts"),
        to: "/payouts",
        hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Payout_ListPayouts),
      },
      {
        title: t("menu.reporting.reports"),
        to: "/reports",
        hasAccess: () => authorizationStore.hasAnySiteScoped(Permission.Report_ListReports),
      },
      {
        title: t("menu.reporting.eod-reports"),
        to: "/eod-reports",
        hasAccess: () =>
          !!posthog?.isFeatureEnabled("eod-report") &&
          authorizationStore.hasAnySiteScoped(Permission.EODReport_ListReports),
      },
    ],
  },
  {
    title: t("menu.billing.title"),
    icon: "mdi-cash",
    hasAccess: () => authorizationStore.canManageCompanies,
    pages: [
      {
        title: t("menu.billing.paymentMethods"),
        to: "/billing/payment-methods",
        hasAccess: () => authorizationStore.hasCompanyScoped(Permission.Billing_ListPaymentMethods),
      },
      {
        title: t("menu.billing.invoices"),
        to: "/billing/invoices",
        hasAccess: () => authorizationStore.hasCompanyScoped(Permission.Billing_ListInvoices),
      },
    ],
  },
  {
    title: t("menu.users.title"),
    icon: "mdi-account-group",
    to: "/users",
    hasAccess: () => authorizationStore.hasCompanyScoped(Permission.User_ListUsers),
  },
  {
    title: t("menu.company.title"),
    icon: "mdi-domain",
    hasAccess: () => authorizationStore.canManageCompanies,
    pages: [
      {
        title: t("menu.company.settings"),
        to: "/company-settings",
      },
      {
        title: t("menu.company.manageCompanies"),
        to: "/companies",
        hide: merchantsStore.merchants.length < 2,
      },
      {
        title: t("menu.company.addCompany"),
        atClick: () => (addCompanyModal.value = true),
      },
    ],
  },
]);
</script>

<style scoped>
.no-underline {
  text-decoration: none;
}
</style>
